import PropTypes from 'prop-types';
import { composeEvent } from '@icp/utils';
import { InputNumber } from 'antd';
import { HELPER_TEXT_TYPES, toDecimalPrecision } from '@icp/form-renderer-core';
import { useState, useEffect, useRef } from 'react';
import FieldTitle from '../../FieldTitle';
import FormHelperText from '../../FormHelperText';

function NumberPickerAnt(props) {
  const {
    id,
    title,
    value: valueProp,
    disabled,
    readonly,
    componentProps,
    fieldTitleProps,
    validation,
    status,
    helpers,
    onChange,
    onTouchChanged,
    onBlur,
    onKeyDown,
    onKeyUp,
    min,
    max,
    step,
    precision,
    readonlyText,
  } = props;

  const { changeDelayToBlur, stringMode, ...otherComponentProps } = componentProps;

  const [value, setValue] = useState(valueProp);
  // 让 keydown 和 keyup 之间 onChange 只会触发一次，防止一致按住 Enter 键反复触发 onChange
  const once = useRef(false);

  useEffect(() => {
    setValue(valueProp);
  }, [valueProp]);

  const handleChange = (newValue) => {
    setValue(newValue);

    if (!changeDelayToBlur) {
      onChange(newValue);
    }
  };

  const handleBlur = () => {
    if (changeDelayToBlur) {
      // antd 自带 precision 逻辑，但是它的实现方式是在 blur 过后多触发一次 onChange 处理了 precision 的结果，
      // 当 changeDelayToBlur true，这里就接受不到最后这次都触发的结果，所以只能和 material ui 一样在这里主动处理
      onChange(toDecimalPrecision(value, precision, stringMode));
    }
    if (onTouchChanged) {
      onTouchChanged();
    }
  };

  // 当 changeDelayToBlur 时，按回车键也需要触发 onChange。和原生 input 逻辑一致
  const handleKeyDown = (event) => {
    if (once.current) {
      return;
    }

    if (event.key === 'Enter') {
      once.current = true;
      handleBlur();
    }
  };

  const handleKeyUp = (event) => {
    if (event.key === 'Enter') {
      once.current = false;
    }
  };

  return (
    <>
      <FieldTitle required={validation?.required} {...fieldTitleProps}>
        {title}
      </FieldTitle>
      <div>
        {readonly ? (
          <span className="readonly-text">{readonlyText}</span>
        ) : (
          <InputNumber
            type="number"
            {...otherComponentProps}
            stringMode={stringMode}
            name={id}
            value={value}
            min={min}
            max={max}
            step={step}
            precision={precision}
            disabled={disabled}
            onChange={handleChange}
            onBlur={composeEvent(handleBlur, onBlur)}
            onKeyDown={composeEvent(handleKeyDown, onKeyDown)}
            onKeyUp={composeEvent(handleKeyUp, onKeyUp)}
            status={status}
          />
        )}
        <FormHelperText helpers={helpers} />
      </div>
    </>
  );
}

NumberPickerAnt.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  componentProps: PropTypes.shape({
    changeDelayToBlur: PropTypes.bool,
    stringMode: PropTypes.bool,
  }),
  fieldTitleProps: PropTypes.shape({}),
  validation: PropTypes.shape({
    required: PropTypes.bool,
  }),
  disabled: PropTypes.bool,
  readonly: PropTypes.bool,
  status: PropTypes.oneOf(HELPER_TEXT_TYPES),
  helpers: PropTypes.arrayOf(
    PropTypes.shape({
      status: PropTypes.oneOf(HELPER_TEXT_TYPES),
      text: PropTypes.string,
    }),
  ),
  onChange: PropTypes.func,
  onTouchChanged: PropTypes.func,
  onBlur: PropTypes.func,
  onKeyDown: PropTypes.func,
  onKeyUp: PropTypes.func,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  precision: PropTypes.number,
  readonlyText: PropTypes.string,
};

export default NumberPickerAnt;
